import {useEffect, useDebugValue, useState, useLayoutEffect, useRef, useMemo} from 'react';
import {listen} from 'polyphony/dom';
import {executeAfter, callOnce, combineRefs, uuidv4} from 'polyphony/util';
import {matchesSelector, observeIntersection} from 'polyphony/dom';

export const useUnique = () => {
    return useMemo(() => uuidv4(), []);
};

export const useJavaScript = (parent, js) => {
    useDebugValue(js);
    useEffect(() => {
        if (!js || js.length === 0) {
            return () => {};
        }
        let x = document.createElement('script');
        x.textContent = `/*<![CDATA[*/${js}/*]]>*/`;
        parent.appendChild(x);
        return () => parent.removeChild(x);
    }, [js]);
};

/*
    useDelay
    Not strictly a DOM hook, but it does use browser-specific nonsense.
*/

export const useDelay = (timeout = 400) => {
    const [delayed, setDelayed] = useState(false);
    useLayoutEffect(() => executeAfter(() => setDelayed(true), timeout), []);
    return delayed;
};

/*
    useListener
*/

export const useListener = (domElement, event, callback, callbackMemo = [], opts = {}) => {
    return useLayoutEffect(() => listen(domElement, event, callback, opts),
                                        [domElement, event, opts && opts.capture, opts && opts.passive, ...callbackMemo]);
};

/*
    useRefWithListeners
    Like useRef, but allows event listeners to be attached
    without conflicting with props' listeners.

    e.g

    const MyComponent = ({...props}) => {
        const ref = useRefWithListeners({
            click: e => {

            },
            mouseover: e => {

            }
        });

        return <div ref={ref} />;
    };
*/

export const useRefWithListeners = (listeners, memo = [], passive = false) => {
    const callables = Object.entries(listeners).map(([k, v]) => el => listen(el, k, v, {passive}));

    useDebugValue(listeners, ls => Object.keys(ls).join(', '));

    const ref = useRef();
    useLayoutEffect(() => {
        const el = ref.current;
        if (el) {
            const cancels = callables.map(c => c(el));
            return () => cancels.map(f => f());
        }
        return () => {};
    }, [ref.current, Object.keys(listeners).join('__'), ...memo]);
    return ref;
};

/*
    useHotkeys
*/

export const useHotkeys = (keys, memo=[]) => {
    let ast = {};
    for (const [shortcut, action] of Object.entries(keys)) {
        const all = Array.isArray(shortcut) ? shortcut : shortcut.trim().split('-').map(s => s.trim()).map(s => s === 'Space' ? ' ' : s);
        const key = all[all.length - 1].toUpperCase();
        const modifiers = all.slice(0, all.length - 1);

        ast[key] = ast[key] || [];
        ast[key].push({modifiers, action});
    }

    const ref = useRefWithListeners({
        click: e => e.target.focus(),
        keydown: (...args) => {
            const [e] = args;
            if (e.isComposing) return;
            const hit = ast[e.key.toUpperCase()];
            if (!hit) return;
            for (const {modifiers, action} of hit) {
                if (modifiers.includes('Shift') && !e.shiftKey) continue;
                if (modifiers.includes('Alt') && !e.altKey) continue;
                if (modifiers.includes('Ctrl') && !e.ctrltKey) continue;
                if (modifiers.includes('Meta') && !e.metaKey) continue;
                action(...args);
            }
        }
    }, memo);

    // Ensure screen readers can focus the
    // DOM node with the shortcuts
    const ensureTabindex = r => {
        if (r && !r.getAttribute('tabindex')) r.setAttribute('tabindex', '0');
    };

    return combineRefs([ensureTabindex, ref]);
};

/*
    useHover
*/

export const useHover = (memo = []) => {
    const [hover, setHover] = useState(false);
    const [active, setActive] = useState(false);
    const ref = useRefWithListeners({
        mouseenter: e => { // eslint-disable-line no-unused-vars
            setHover(true);
        },
        mouseleave: e => { // eslint-disable-line no-unused-vars
            setHover(false);
            setActive(false);
        },
        mouseup: e => { // eslint-disable-line no-unused-vars
            setActive(false);
        },
        mousedown: e => { // eslint-disable-line no-unused-vars
            setActive(true);
        }
    }, memo);

    useDebugValue(`${hover ? 'hover' : 'not hover'}, ${active ? 'active' : 'not active'}`);
    return [{hover, active}, combineRefs([ref, r => {
        // When the ref changes, we need to get the initial
        // hover/active state of the newly ref'd node.
        if (r) {
            setHover(matchesSelector(r, ":hover"));
            setActive(matchesSelector(r, ":active"));
        } else {
            // clear out values for the old DOM node.
            setHover(false);
            setActive(false);
        }
    }])];
};

export const useFocus = (memo = []) => {
    const [isFocused, setFocused] = useState();
    let ref = useRefWithListeners({
        focus: () => setFocused(true),
        blur: () => setFocused(false)
    }, memo);

    return [isFocused, combineRefs([ref, r => {
        if (r) {
            setFocused(matchesSelector(r, ':focus'));
        } else {
            setFocused(false);
        }
    }])];
};

/*
    useInitialVisibility
*/

export const useInitialVisibility = (callback, memo = []) => {
    const ref = useRef();

    useLayoutEffect(() => {
        const element = ref.current;
        if (element) {
            let threshold = [];
            for (let i = 0; i <= 1; i += 0.1) threshold.push(i);

            let realCancel = null;
            const {cancel: _cancel, observe} = observeIntersection(nodes => {
                const isVisible = nodes.includes(element);
                if (isVisible) {
                    callback(element);
                    realCancel();
                }
            }, {threshold});
            observe(element);
            realCancel = callOnce(_cancel);
            return realCancel;
        }
        return () => {};
    }, [ref.current, ...memo]);


    return ref;
};

/*
    useScroll

    Returns the scroll position
*/
listen(window, 'scroll', () => {
    polyphony.recordWindowPosition(pageXOffset, pageYOffset);
}, {passive: true, capture: true});

const roundedEqual = numPlaces => (a, b) => {
     const factor = Math.pow(10, numPlaces);
     const ap = Math.trunc(a * factor);
     const bp = Math.trunc(b * factor);
     return ap === bp;
};

export const useScroll = () => {
    const y = polyphony.hooks.useSelector(s => s.windowY, roundedEqual(2));
    const x = polyphony.hooks.useSelector(s => s.windowX, roundedEqual(2));
    const v = {x, y};
    useDebugValue(v, () => `${v.x}x${v.y}`);
    return v;
};


/*
    useResize

    Returns the viewport size
*/

listen(window, 'resize', () => {
    polyphony.recordWindowSize(innerWidth, innerHeight);
}, {passive: true, capture: true});

export const useResize = () => {
    const width = polyphony.hooks.useSelector(s => s.windowWidth, roundedEqual(2));
    const height = polyphony.hooks.useSelector(s => s.windowHeight, roundedEqual(2));
    const v = {width, height};
    useDebugValue(v, () => `${v.width}x${v.height}`);
    return v;
};

/*
    useBottom

    Returns whether or not the page is scrolled to the bottom.
*/

export const useBottom = (threshold = 100) => {
    const toward = useRef(true);
    const away = useRef(false);
    const [isBottom, setBottom] = useState(false);
    useListener(window, 'scroll', () => {
        let tholdValue = typeof threshold === 'function' ? threshold() : threshold;
        let difference = document.documentElement.scrollHeight - (pageYOffset + innerHeight);

        let is = difference < tholdValue;

        if (is) {
            if (toward.current) {
                toward.current = false;
                away.current = true;
                setBottom(true);
            }
        } else if (away.current) {
            toward.current = true;
            away.current = false;
            setBottom(false);
        }
    }, [], {passive: true, capture: true});
    useDebugValue(isBottom, () => isBottom ? 'true' : 'false');
    return isBottom;
};

